import { useState, useCallback } from 'react';
import { Button, Card, CardBody } from '@kollektive/design-system';
import { X, ChevronRight, ChevronLeft, Check, Database, Key, Users, Sparkles } from 'lucide-react';
import { useOnboarding } from '@/hooks/useOnboarding';
import styles from './OnboardingWizard.module.css';

interface OnboardingWizardProps {
  onClose?: () => void;
}

type Step = 'welcome' | 'connection' | 'token' | 'invite' | 'complete';

const STEPS: Step[] = ['welcome', 'connection', 'token', 'invite', 'complete'];

export function OnboardingWizard({ onClose }: OnboardingWizardProps) {
  const { completeOnboarding, skipOnboarding } = useOnboarding();
  const [currentStep, setCurrentStep] = useState<Step>('welcome');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentStepIndex = STEPS.indexOf(currentStep);

  const handleNext = useCallback(() => {
    const nextIndex = currentStepIndex + 1;
    const nextStep = STEPS[nextIndex];
    if (nextStep) {
      setCurrentStep(nextStep);
    }
  }, [currentStepIndex]);

  const handlePrevious = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    const prevStep = STEPS[prevIndex];
    if (prevStep) {
      setCurrentStep(prevStep);
    }
  }, [currentStepIndex]);

  const handleSkip = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await skipOnboarding();
      onClose?.();
    } catch {
      // Error handled by hook
    } finally {
      setIsSubmitting(false);
    }
  }, [skipOnboarding, onClose]);

  const handleComplete = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await completeOnboarding();
      onClose?.();
    } catch {
      // Error handled by hook
    } finally {
      setIsSubmitting(false);
    }
  }, [completeOnboarding, onClose]);

  const renderStepIndicator = () => (
    <div className={styles.stepIndicator}>
      {STEPS.slice(0, -1).map((step, index) => (
        <div
          key={step}
          className={`${styles.stepDot} ${index <= currentStepIndex ? styles.stepDotActive : ''} ${index < currentStepIndex ? styles.stepDotComplete : ''}`}
        />
      ))}
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 'welcome':
        return (
          <div className={styles.stepContent}>
            <div className={styles.stepIcon}>
              <Sparkles size={48} />
            </div>
            <h2 className={styles.stepTitle}>Welcome to Kollektive!</h2>
            <p className={styles.stepDescription}>
              Let's get your workspace set up. We'll guide you through connecting your first database
              and generating an API token to start using Kollektive MCP.
            </p>
            <div className={styles.stepHighlights}>
              <div className={styles.highlight}>
                <Database size={20} />
                <span>Connect a database</span>
              </div>
              <div className={styles.highlight}>
                <Key size={20} />
                <span>Generate API token</span>
              </div>
              <div className={styles.highlight}>
                <Users size={20} />
                <span>Invite your team</span>
              </div>
            </div>
          </div>
        );

      case 'connection':
        return (
          <div className={styles.stepContent}>
            <div className={styles.stepIcon}>
              <Database size={48} />
            </div>
            <h2 className={styles.stepTitle}>Connect Your Database</h2>
            <p className={styles.stepDescription}>
              Head over to the Connections page to add your first database connection.
              We support PostgreSQL and other popular databases.
            </p>
            <div className={styles.stepInstructions}>
              <ol>
                <li>Click on "Connections" in the sidebar</li>
                <li>Click "New Connection"</li>
                <li>Enter your database credentials</li>
                <li>Test and save the connection</li>
              </ol>
            </div>
            <Button
              variant="secondary"
              onClick={() => window.open('/connections', '_blank')}
              className={styles.actionButton}
            >
              Open Connections Page
            </Button>
          </div>
        );

      case 'token':
        return (
          <div className={styles.stepContent}>
            <div className={styles.stepIcon}>
              <Key size={48} />
            </div>
            <h2 className={styles.stepTitle}>Generate an API Token</h2>
            <p className={styles.stepDescription}>
              Create an API token to authenticate your MCP client.
              This token gives your AI assistant access to query your databases.
            </p>
            <div className={styles.stepInstructions}>
              <ol>
                <li>Go to the Tokens page</li>
                <li>Click "Create Token"</li>
                <li>Give it a descriptive name</li>
                <li>Copy and securely store the token</li>
              </ol>
            </div>
            <Button
              variant="secondary"
              onClick={() => window.open('/tokens', '_blank')}
              className={styles.actionButton}
            >
              Open Tokens Page
            </Button>
          </div>
        );

      case 'invite':
        return (
          <div className={styles.stepContent}>
            <div className={styles.stepIcon}>
              <Users size={48} />
            </div>
            <h2 className={styles.stepTitle}>Invite Your Team</h2>
            <p className={styles.stepDescription}>
              Collaboration is better together! Invite team members to help manage
              connections and share access to your databases.
            </p>
            <div className={styles.stepNote}>
              You can always invite team members later from the Team page.
            </div>
          </div>
        );

      case 'complete':
        return (
          <div className={styles.stepContent}>
            <div className={styles.stepIconSuccess}>
              <Check size={48} />
            </div>
            <h2 className={styles.stepTitle}>You're All Set!</h2>
            <p className={styles.stepDescription}>
              Your workspace is ready to go. Start querying your databases with
              natural language through Kollektive MCP.
            </p>
            <div className={styles.stepHighlights}>
              <div className={styles.highlight}>
                <Check size={20} />
                <span>Workspace configured</span>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.overlay}>
      <Card className={styles.wizard}>
        <CardBody className={styles.wizardBody}>
          {/* Close button */}
          <button
            className={styles.closeButton}
            onClick={handleSkip}
            disabled={isSubmitting}
            aria-label="Close"
          >
            <X size={20} />
          </button>

          {/* Step indicator */}
          {renderStepIndicator()}

          {/* Step content */}
          {renderStepContent()}

          {/* Navigation */}
          <div className={styles.navigation}>
            {currentStep !== 'welcome' && currentStep !== 'complete' && (
              <Button
                variant="ghost"
                onClick={handlePrevious}
                disabled={isSubmitting}
              >
                <ChevronLeft size={16} />
                Back
              </Button>
            )}

            <div className={styles.navigationSpacer} />

            {currentStep === 'welcome' && (
              <Button
                variant="ghost"
                onClick={handleSkip}
                disabled={isSubmitting}
              >
                Skip setup
              </Button>
            )}

            {currentStep !== 'complete' ? (
              <Button
                variant="primary"
                onClick={handleNext}
                disabled={isSubmitting}
              >
                {currentStep === 'invite' ? 'Finish' : 'Next'}
                <ChevronRight size={16} />
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleComplete}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Get Started'}
              </Button>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
